<template>
    <v-dialog :model-value="modelValue" persistent width="560"
              @update:model-value="$emit('update:modelValue', $event)">
        <v-card>
            <v-card-title class="d-flex justify-space-between align-center">
                <span>{{ credential ? 'Edit credential' : 'Add credential' }}</span>
                <v-btn icon="mdi-close" variant="text" density="compact" @click="close"/>
            </v-card-title>

            <v-form v-model="valid" @submit.prevent="save">
                <v-card-text>
                    <v-row dense>
                        <v-col cols="12" sm="6">
                            <v-select v-model="form.type" :items="typeOptions" item-title="label" item-value="value"
                                      :rules="[v => !!v || 'Type is required']" density="compact" label="Type *"/>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="form.identifier" density="compact"
                                          label="Number / reference"/>
                        </v-col>
                        <v-col cols="12">
                            <v-text-field v-model="form.title" density="compact"
                                          label="Title / description"/>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="form.issued_on" type="date" density="compact" label="Issued on"/>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="form.expires_on" type="date" density="compact" label="Expires on"/>
                        </v-col>
                        <v-col cols="12">
                            <v-file-input v-model="form.document" accept=".pdf,image/*" density="compact"
                                          prepend-icon="mdi-paperclip" label="Document (PDF or image)"
                                          :hint="credential?.has_document ? `Current: ${credential.document_name || 'file on record'}` : ''"
                                          persistent-hint show-size/>
                        </v-col>
                        <v-col cols="12">
                            <v-textarea v-model="form.notes" density="compact" rows="2" label="Notes"/>
                        </v-col>
                    </v-row>
                </v-card-text>

                <v-card-actions class="px-4 pb-3 d-flex justify-end">
                    <v-btn variant="text" @click="close">Cancel</v-btn>
                    <v-btn color="primary" type="submit" :loading="saving" :disabled="!valid">Save</v-btn>
                </v-card-actions>
            </v-form>
        </v-card>
    </v-dialog>
</template>

<script setup>
import {reactive, ref} from "vue";
import {refreshMenuCounts} from "@/composables/useMenuCounts";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";

const props = defineProps({
    modelValue: Boolean,
    driverId: {type: [String, Number], required: true},
    credential: {type: Object, default: null},
});
const emit = defineEmits(["update:modelValue", "saved"]);
const {showSnackbar} = useSnackbarStore();

const typeOptions = [
    {value: "cdl", label: "CDL / License"},
    {value: "medical_card", label: "DOT Medical Card"},
    {value: "mvr", label: "Motor Vehicle Record"},
    {value: "clearinghouse", label: "Clearinghouse Query"},
    {value: "drug_alcohol", label: "Drug & Alcohol Test"},
    {value: "hazmat", label: "Hazmat Endorsement"},
    {value: "training", label: "Training / Certification"},
    {value: "other", label: "Other"},
];

const valid = ref(false);
const saving = ref(false);
const form = reactive({
    type: props.credential?.type ?? null,
    title: props.credential?.title ?? null,
    identifier: props.credential?.identifier ?? null,
    issued_on: props.credential?.issued_on ?? null,
    expires_on: props.credential?.expires_on ?? null,
    notes: props.credential?.notes ?? null,
    document: null,
});

const close = () => emit("update:modelValue", false);

const save = () => {
    if (!valid.value) return;
    saving.value = true;

    const fd = new FormData();
    fd.append("type", form.type);
    // Always send the editable fields, even when blank, so clearing one on edit
    // actually clears it. The API converts empty strings to null (nullable rules).
    ["title", "identifier", "issued_on", "expires_on", "notes"].forEach((k) => {
        fd.append(k, form[k] ?? "");
    });
    if (form.document) fd.append("document", form.document);

    const base = `companies/drivers/${props.driverId}/credentials`;
    const url = props.credential ? `${base}/${props.credential.id}` : base;

    send("post", url, fd, {headers: {"Content-Type": "multipart/form-data"}})
        .then((response) => {
            showSnackbar(response.data.message);
            emit("saved", response.data.data);
            refreshMenuCounts();
            close();
        })
        .catch((error) => {
            const msg = error.response?.data?.errors
                ? Object.values(error.response.data.errors)[0][0]
                : (error.response?.data?.message ?? "Could not save credential");
            showSnackbar(msg, "error");
        })
        .finally(() => saving.value = false);
};
</script>
